<script setup lang="ts">
import { email } from '@solfacil/frontend-utils'
import IconClose from '~icons/material-symbols/close-rounded'

type ErrorKind = 'invalid' | 'max'

const props = withDefaults(defineProps<{
  modelValue?: string[]
  max?: number
  disabled?: boolean
  error?: string
  label?: string
  placeholder?: string
  helper?: string
  id?: string
  name?: string
}>(), {
  modelValue: () => [],
  max: 3,
  disabled: false,
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: string[]): void
  (e: 'blur', event: FocusEvent): void
}>()

const SEPARATOR_REGEX = /[\s,;]+/
// Typed separators caught on `input`: virtual keyboards report `keydown` as "Unidentified".
const TYPED_SEPARATOR_REGEX = /[,;]/

const { t } = useI18n()
const uid = useId()

const inputRef = ref<HTMLInputElement | null>(null)
const pending = ref('')
const errorKind = ref<ErrorKind | null>(null)

const tags = computed(() => props.modelValue.filter(tag => typeof tag === 'string' && tag.trim() !== ''))
const hasPending = computed(() => pending.value.trim() !== '')
const isFull = computed(() => tags.value.length >= props.max)
// Full: the input is visually hidden but stays focusable, so Backspace still removes the last tag.
const isLocked = computed(() => isFull.value && !hasPending.value)

const inputId = computed(() => props.id ?? `email-tags-${uid}`)
const messageId = computed(() => `${inputId.value}-message`)

const resolvedPlaceholder = computed(() => props.placeholder ?? t('components.email_tags_input.placeholder'))
const maxReachedMessage = computed(() => t('components.email_tags_input.max_reached', { max: props.max }))

const errorMessage = computed(() => {
  if (errorKind.value === 'invalid')
    return t('components.email_tags_input.invalid_email')
  if (errorKind.value === 'max')
    return maxReachedMessage.value
  return props.error ?? ''
})
const hasError = computed(() => errorMessage.value !== '')

const message = computed(() => {
  if (hasError.value)
    return errorMessage.value
  if (isFull.value && !props.disabled)
    return maxReachedMessage.value
  return props.helper ?? t('components.email_tags_input.helper')
})

function isSameEmail(first: string, second: string) {
  return email.normalize(first) === email.normalize(second)
}

function resolveErrorKind(hasInvalid: boolean, hasOverflow: boolean): ErrorKind | null {
  if (hasInvalid)
    return 'invalid'
  if (hasOverflow)
    return 'max'
  return null
}

function commit(raw: string): boolean {
  const candidates = raw.split(SEPARATOR_REGEX).filter(Boolean)
  const next = [...tags.value]
  const rejected: string[] = []
  let hasInvalid = false
  let hasOverflow = false

  for (const candidate of candidates) {
    if (next.some(tag => isSameEmail(tag, candidate)))
      continue

    if (!email.validInput(candidate)) {
      rejected.push(candidate)
      hasInvalid = true
    }
    else if (next.length >= props.max) {
      rejected.push(candidate)
      hasOverflow = true
    }
    else {
      next.push(candidate)
    }
  }

  if (next.length !== tags.value.length)
    emit('update:modelValue', next)

  pending.value = rejected.join(' ')
  errorKind.value = resolveErrorKind(hasInvalid, hasOverflow)
  return rejected.length === 0
}

function focusInput() {
  if (props.disabled)
    return
  nextTick(() => inputRef.value?.focus())
}

function removeAt(index: number) {
  if (props.disabled)
    return
  emit('update:modelValue', tags.value.filter((_, tagIndex) => tagIndex !== index))
  if (errorKind.value === 'max')
    errorKind.value = null
  focusInput()
}

function onInput(event: Event) {
  const { value } = event.target as HTMLInputElement
  pending.value = value
  errorKind.value = null
  if (!(event as InputEvent).isComposing && TYPED_SEPARATOR_REGEX.test(value))
    commit(value)
}

function onKeydown(event: KeyboardEvent) {
  if (props.disabled || event.isComposing)
    return

  if (event.key === 'Enter' || event.key === ',') {
    event.preventDefault()
    if (hasPending.value)
      commit(pending.value)
    return
  }

  if (event.key === 'Backspace' && pending.value === '' && tags.value.length > 0)
    removeAt(tags.value.length - 1)
}

function onPaste(event: ClipboardEvent) {
  const pasted = event.clipboardData?.getData('text/plain') ?? ''
  if (props.disabled || !SEPARATOR_REGEX.test(pasted.trim()))
    return

  event.preventDefault()
  const target = event.target as HTMLInputElement
  const start = target.selectionStart ?? pending.value.length
  const end = target.selectionEnd ?? start
  commit(`${pending.value.slice(0, start)}${pasted}${pending.value.slice(end)}`)
}

function onBlur(event: FocusEvent) {
  if (!props.disabled && hasPending.value)
    commit(pending.value)
  emit('blur', event)
}

function commitPending(): boolean {
  if (props.disabled || !hasPending.value)
    return true
  return commit(pending.value)
}

defineExpose({
  commitPending,
  focus: focusInput,
})
</script>

<template>
  <div
    class="email-tags-input"
    :class="{ 'email-tags-input--disabled': disabled }"
    :data-testid="`email-tags-input-${inputId}`"
  >
    <label
      v-if="label"
      :for="inputId"
      class="email-tags-input__label"
    >
      {{ label }}
    </label>

    <div
      class="email-tags-input__field"
      :class="{
        'email-tags-input__field--error': hasError,
        'email-tags-input__field--locked': isLocked,
        'email-tags-input__field--disabled': disabled,
      }"
      @click="focusInput"
    >
      <ul
        v-if="tags.length"
        role="list"
        class="email-tags-input__list"
        :aria-label="t('components.email_tags_input.list_aria')"
      >
        <li
          v-for="(tag, index) in tags"
          :key="`${index}-${tag}`"
          role="listitem"
          class="email-tags-input__chip"
          :title="tag"
          :data-testid="`email-tags-input-chip-${index}`"
        >
          <span class="email-tags-input__chip-label">
            {{ tag }}
          </span>
          <button
            v-if="!disabled"
            type="button"
            class="email-tags-input__chip-remove"
            :aria-label="t('components.email_tags_input.remove_aria', { email: tag })"
            :data-testid="`email-tags-input-remove-${index}`"
            @click.stop="removeAt(index)"
          >
            <IconClose aria-hidden="true" />
          </button>
        </li>
      </ul>

      <input
        :id="inputId"
        ref="inputRef"
        class="email-tags-input__input"
        :class="{ 'email-tags-input__input--locked': isLocked }"
        type="text"
        inputmode="email"
        autocomplete="off"
        autocapitalize="off"
        spellcheck="false"
        :name="name"
        :value="pending"
        :placeholder="tags.length ? '' : resolvedPlaceholder"
        :disabled="disabled"
        :readonly="isLocked"
        :aria-label="label ? undefined : resolvedPlaceholder"
        :aria-describedby="message ? messageId : undefined"
        :aria-invalid="hasError ? 'true' : undefined"
        :data-testid="`email-tags-input-field-${inputId}`"
        @input="onInput"
        @keydown="onKeydown"
        @paste="onPaste"
        @blur="onBlur"
      >
    </div>

    <div
      class="email-tags-input__message"
      aria-live="polite"
    >
      <p
        v-if="message"
        :id="messageId"
        class="email-tags-input__message-text !mx-0"
        :class="{ 'email-tags-input__message-text--error': hasError }"
        :data-testid="`email-tags-input-message-${inputId}`"
      >
        {{ message }}
      </p>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.email-tags-input {
  @apply flex flex-col gap-nano;

  &__label {
    @apply fonts-body-medium-regular text-neutral-low-dark;
  }

  &__field {
    @apply relative flex flex-wrap items-center gap-nano;
    @apply min-h-md px-micro py-nano;
    @apply rounded-md border-a-xs border-neutral-high-medium bg-neutral-high-pure;
    @apply cursor-text transition-colors;

    &:focus-within {
      @apply border-neutral-low-dark;
    }

    &--error,
    &--error:focus-within {
      @apply border-feedback-negative-pure;
    }

    &--locked {
      @apply cursor-default;
    }

    &--disabled {
      @apply cursor-not-allowed opacity-intense;
    }
  }

  &__list {
    @apply flex flex-wrap items-center gap-nano;
    @apply min-w-0 max-w-full m-0 p-0 list-none;
  }

  &__chip {
    @apply flex items-center gap-quark min-w-0 max-w-full;
    @apply px-micro py-[6px] rounded-sm bg-neutral-high-light;
  }

  &__chip-label {
    @apply truncate fonts-body-medium-regular text-neutral-low-dark;
  }

  &__chip-remove {
    @apply flex flex-shrink-0 items-center justify-center;
    @apply p-quark -m-quark border-0 rounded-sm bg-transparent cursor-pointer;
    @apply text-3xs text-neutral-low-medium transition-colors;

    &:hover {
      @apply text-neutral-low-dark;
    }

    &:focus-visible {
      outline: 2px solid theme('colors.highlight.pure');
      outline-offset: 2px;
    }
  }

  &__input {
    @apply flex-1 min-w-[200px] p-0 border-0 outline-none bg-transparent;
    @apply fonts-body-large-regular text-neutral-low-medium;

    &::placeholder {
      @apply text-neutral-high-dark;
    }

    &:disabled {
      @apply cursor-not-allowed;
    }

    &--locked {
      @apply sr-only min-w-0;
    }
  }

  &__message {
    @apply min-h-3xs;
  }

  &__message-text {
    @apply m-0 fonts-body-small-regular text-neutral-low-light;

    &--error {
      @apply text-feedback-negative-pure;
    }
  }
}
</style>
